/**
 * Routing Schemas - Browser-Safe Exports
 * 
 * This file contains Zod schemas for SIP Routing Rule validation.
 * It is safe to import in browser environments (React, Vite, etc.)
 * 
 * RFC 3261 Compliance (Section 16 - Proxy Behavior):
 * - Route header processing and manipulation
 * - Record-Route insertion for dialog maintenance
 * - Max-Forwards processing
 * - Via header insertion
 * 
 * CRITICAL: Do NOT import anything from:
 * - '../sip/' (contains database operations)
 * - '../drizzle/' (database connection)
 * - Any other Node.js-only modules
 * 
 * @see RFC 3261 Section 16 - Proxy Behavior
 * @packageDocumentation
 */

import { z } from 'zod';
import { optionalNumber, optionalNumberInRange } from './transforms';

// ========================================
// SUB-SCHEMAS
// ========================================

/**
 * Number Pattern Type - user-friendly pattern matching
 * These are transformed to regex in the engine:
 * - 'any': match all (.*)
 * - 'prefix': starts with (^value)
 * - 'suffix': ends with (value$)
 * - 'exact': exact match (^value$)
 * - 'contains': contains (.*value.*)
 * - 'regex': raw regex (advanced users)
 */
export const NumberPatternTypeSchema = z.enum(['any', 'prefix', 'suffix', 'exact', 'contains', 'regex']);

/**
 * Single Number Pattern - called or calling number
 */
export const SingleNumberPatternSchema = z.object({
  type: NumberPatternTypeSchema,
  value: z.string().default('')
});

/**
 * Number Pattern - combines called (To) and calling (From) patterns
 */
export const NumberPatternSchema = z.object({
  called: SingleNumberPatternSchema.optional(),  // Called number (To/Request-URI)
  calling: SingleNumberPatternSchema.optional()  // Calling number (From)
}).optional();

/**
 * Source Type - who sends the call
 * Primary (shown in main UI): 'any', 'sip_peer', 'peer_group'
 * Advanced (hidden by default): 'ip_range', 'user_agent', 'from_header'
 */
export const SourceTypeSchema = z.enum([
  'any',          // Match all sources
  'sip_peer',     // Match specific SIP peer
  'peer_group',   // Match any peer in a group
  'ip_range',     // Advanced: Match by IP/CIDR
  'user_agent',   // Advanced: Match by User-Agent
  'from_header'   // Advanced: Match by From header pattern
]);

/**
 * Time Window for time-based routing
 * Allows routing rules to be active only during specific hours
 */
export const TimeWindowSchema = z.object({
  start: z.string(), // HH:MM format
  end: z.string(),   // HH:MM format
  timezone: z.string().optional(),
});

/**
 * Routing Conditions
 * Conditions that must match for the routing rule to apply
 */
export const RoutingConditionsSchema = z.object({
  // Pattern matching on SIP headers (legacy - prefer numberPattern)
  fromPattern: z.string().optional(),
  toPattern: z.string().optional(),
  userAgent: z.string().optional(),
  
  // Time-based routing
  timeWindow: TimeWindowSchema.optional(),
}).optional();

/**
 * Routing Actions
 * Actions to perform when the routing rule matches
 * 
 * NOTE: rewriteFrom/rewriteTo removed - use manipulation profiles instead
 */
export const RoutingActionsSchema = z.object({
  // Header manipulation
  addHeaders: z.record(z.string(), z.string()).optional(),
  removeHeaders: z.array(z.string()).optional(),
  
  // Codec enforcement
  enforceCodecs: z.array(z.string()).optional(),
}).optional();

/**
 * RFC 3261 Specific Conditions
 * Additional conditions based on RFC 3261 headers
 */
export const RfcConditionsSchema = z.object({
  /** Only match if Route header is present */
  requireRoute: z.boolean().optional(),
  
  /** Pattern to match Route header */
  routePattern: z.string().optional(),
  
  /** Minimum Max-Forwards value required (0-255 per RFC 3261) */
  maxForwardsMin: optionalNumberInRange(0, 255),
  
  /** Only match if this is an in-dialog request (has Route from Record-Route) */
  requireRecordRoute: z.boolean().optional(),
}).optional();

// ========================================
// MAIN SCHEMAS
// ========================================

/**
 * Routing Rule Create Input - SINGLE SOURCE OF TRUTH
 * 
 * Used by:
 * - Backend API: zValidator('json', RoutingRuleCreateInputSchema)
 * - Frontend: zodResolver(RoutingRuleCreateInputSchema)
 * 
 * This schema validates input for creating a new routing rule.
 */
export const RoutingRuleCreateInputSchema = z.object({
  // Basic info
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().optional(),
  enabled: z.boolean().default(true),
  priority: z.number().min(1).max(999),
  
  // ========================================
  // SOURCE MATCHING (WHO sends the call)
  // ========================================
  hostId: z.string().uuid().nullable().optional(), // Optional host scoping
  sourceScope: z.enum(['any', 'sip_peer', 'peer_group']).optional(),
  sourceType: SourceTypeSchema.default('any'),
  sourcePattern: z.string().default('*'), // Legacy: raw pattern for advanced use
  sourcePeerId: z.string().uuid().nullable().optional(),      // FK when sourceType = 'sip_peer'
  sourcePeerGroupId: z.string().uuid().nullable().optional(), // FK when sourceType = 'peer_group'
  
  // ========================================
  // NUMBER PATTERN MATCHING (WHAT numbers)
  // ========================================
  numberPattern: NumberPatternSchema,
  
  // ========================================
  // DESTINATION ROUTING
  // ========================================
  destinationType: z.enum(['peer', 'peerGroup', 'reject']).default('peer'),
  destinationTarget: z.string().default(''),
  
  // ========================================
  // ADDITIONAL CONDITIONS (legacy/advanced)
  // ========================================
  conditions: RoutingConditionsSchema,
  actions: RoutingActionsSchema,
  
  // SIP Manipulation profiles (FK references)
  inboundManipulationId: z.string().uuid().nullable().optional(),
  outboundManipulationId: z.string().uuid().nullable().optional(),
  
  // ========================================
  // RFC 3261 Configuration (Section 16)
  // ========================================
  
  /**
   * RFC 3261 §16.6 step 4: Record-Route Insertion
   * If enabled, proxy inserts Record-Route header to stay on dialog path.
   */
  recordRouteEnabled: z.boolean().default(true),
  
  /**
   * RFC 3261 §16.6 step 3: Max-Forwards Decrement
   * If enabled, proxy decrements Max-Forwards by 1 before forwarding.
   */
  decrementMaxForwards: z.boolean().default(true),
  
  /**
   * RFC 3261 §16.6 step 5: Via Header Insertion
   * If enabled, proxy inserts its own Via header before forwarding.
   */
  insertViaHeader: z.boolean().default(true),
  
  /**
   * Pre-loaded Route headers to add to forwarded requests
   * Format: Array of SIP URIs (e.g., ['<sip:proxy1.example.com;lr>'])
   */
  routeHeaders: z.array(z.string()).optional(),
  
  /**
   * RFC 3261 specific routing conditions
   */
  rfcConditions: RfcConditionsSchema,
}).superRefine((data, ctx) => {
  // ========================================
  // SOURCE MATCHING VALIDATION
  // ========================================
  
  // If sourceType is 'sip_peer', sourcePeerId is required
  if (data.sourceType === 'sip_peer' && !data.sourcePeerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a SIP peer',
      path: ['sourcePeerId'],
    });
  }
  
  // If sourceType is 'peer_group', sourcePeerGroupId is required
  if (data.sourceType === 'peer_group' && !data.sourcePeerGroupId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a peer group',
      path: ['sourcePeerGroupId'],
    });
  }

  if (data.sourceScope === 'sip_peer' && !data.sourcePeerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a source SIP peer',
      path: ['sourcePeerId'],
    });
  }

  if (data.sourceScope === 'peer_group' && !data.sourcePeerGroupId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a source peer group',
      path: ['sourcePeerGroupId'],
    });
  }

  if (data.sourceScope === 'any' && (data.sourcePeerId || data.sourcePeerGroupId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Any-source rules cannot include a peer or peer group selector',
      path: ['sourceScope'],
    });
  }
  
  // If sourceType is 'ip_range', sourcePattern must be a valid CIDR or IP
  if (data.sourceType === 'ip_range') {
    const pattern = data.sourcePattern || '';
    // Basic validation: not empty and not just '*'
    if (!pattern || pattern === '*') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid IP address or CIDR range',
        path: ['sourcePattern'],
      });
    }
  }
  
  // If sourceType is 'user_agent', sourcePattern must be non-empty
  if (data.sourceType === 'user_agent') {
    const pattern = data.sourcePattern || '';
    if (!pattern || pattern === '*') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a User-Agent pattern',
        path: ['sourcePattern'],
      });
    }
  }
  
  // If sourceType is 'from_header', sourcePattern must be non-empty
  if (data.sourceType === 'from_header') {
    const pattern = data.sourcePattern || '';
    if (!pattern || pattern === '*') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a From header pattern',
        path: ['sourcePattern'],
      });
    }
  }
  
  // ========================================
  // DESTINATION VALIDATION
  // ========================================
  
  // If destinationType is 'peer' or 'peerGroup', destinationTarget is required
  if ((data.destinationType === 'peer' || data.destinationType === 'peerGroup') && !data.destinationTarget) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: data.destinationType === 'peer' 
        ? 'Please select a destination SIP peer' 
        : 'Please select a destination peer group',
      path: ['destinationTarget'],
    });
  }
});

/**
 * Routing Rule Update Input - SINGLE SOURCE OF TRUTH
 * 
 * All fields optional for partial updates.
 * Separate from Create because required fields differ.
 */
export const RoutingRuleUpdateInputSchema = z.object({
  // All fields optional for partial updates
  name: z.string().min(1).max(100).optional(),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  priority: z.number().min(1).max(999).optional(),
  
  // Source matching
  sourceScope: z.enum(['any', 'sip_peer', 'peer_group']).optional(),
  sourceType: SourceTypeSchema.optional(),
  sourcePattern: z.string().optional(),
  sourcePeerId: z.string().uuid().nullable().optional(),
  sourcePeerGroupId: z.string().uuid().nullable().optional(),
  
  // Number pattern matching
  numberPattern: NumberPatternSchema,
  
  // Destination
  destinationType: z.enum(['peer', 'peerGroup', 'reject']).optional(),
  destinationTarget: z.string().optional(),
  
  // Conditions and Actions
  conditions: RoutingConditionsSchema,
  actions: RoutingActionsSchema,
  
  // SIP Manipulation profiles (FK references)
  inboundManipulationId: z.string().uuid().nullable().optional(),
  outboundManipulationId: z.string().uuid().nullable().optional(),
  
  // RFC 3261 Configuration
  recordRouteEnabled: z.boolean().optional(),
  decrementMaxForwards: z.boolean().optional(),
  insertViaHeader: z.boolean().optional(),
  routeHeaders: z.array(z.string()).optional(),
  rfcConditions: RfcConditionsSchema,
}).superRefine((data, ctx) => {
  if (data.sourceScope === 'sip_peer' && !data.sourcePeerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a source SIP peer',
      path: ['sourcePeerId'],
    });
  }

  if (data.sourceScope === 'peer_group' && !data.sourcePeerGroupId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please select a source peer group',
      path: ['sourcePeerGroupId'],
    });
  }

  if (data.sourceScope === 'any' && (data.sourcePeerId || data.sourcePeerGroupId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Any-source rules cannot include a peer or peer group selector',
      path: ['sourceScope'],
    });
  }
});

/**
 * Phase 4 strict source-selection payloads.
 * Legacy sourceType/sourcePattern are intentionally excluded.
 */
export const RoutingRuleSourceScopeSchema = z.enum(['any', 'sip_peer', 'peer_group']);

type ResolvedSourceScopeRefinementInput = {
  sourceScope?: 'any' | 'sip_peer' | 'peer_group' | undefined;
  sourcePeerId?: string | null | undefined;
  sourcePeerGroupId?: string | null | undefined;
  destinationType?: 'peer' | 'peerGroup' | 'reject' | undefined;
  destinationTarget?: string | null | undefined;
};

function refineResolvedSourceScope(data: ResolvedSourceScopeRefinementInput, ctx: z.RefinementCtx) {
  if (data.sourceScope === 'sip_peer' && data.sourcePeerId == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select a source SIP peer', path: ['sourcePeerId'] });
  }

  if (data.sourceScope === 'peer_group' && data.sourcePeerGroupId == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select a source peer group', path: ['sourcePeerGroupId'] });
  }

  if (data.sourceScope === 'any' && (data.sourcePeerId != null || data.sourcePeerGroupId != null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Any-source rules cannot include a peer or peer group selector', path: ['sourceScope'] });
  }

  if ((data.destinationType === 'peer' || data.destinationType === 'peerGroup') && !data.destinationTarget) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: data.destinationType === 'peer' ? 'Please select a destination SIP peer' : 'Please select a destination peer group',
      path: ['destinationTarget'],
    });
  }
}

function refineRoutingRuleUpdate(data: ResolvedSourceScopeRefinementInput, ctx: z.RefinementCtx) {
  if (data.sourceScope === 'sip_peer' && data.sourcePeerId == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select a source SIP peer', path: ['sourcePeerId'] });
  }

  if (data.sourceScope === 'peer_group' && data.sourcePeerGroupId == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select a source peer group', path: ['sourcePeerGroupId'] });
  }

  if (data.sourceScope === 'any' && (data.sourcePeerId != null || data.sourcePeerGroupId != null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Any-source rules cannot include a peer or peer group selector', path: ['sourceScope'] });
  }

  if ((data.destinationType === 'peer' || data.destinationType === 'peerGroup') && !data.destinationTarget) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: data.destinationType === 'peer' ? 'Please select a destination SIP peer' : 'Please select a destination peer group',
      path: ['destinationTarget'],
    });
  }
}

export const RoutingRuleCreateInputStrictSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().optional(),
  enabled: z.boolean().default(true),
  priority: z.number().min(1).max(999),
  hostId: z.string().uuid().nullable().optional(),
  sourceScope: RoutingRuleSourceScopeSchema.default('any'),
  sourcePeerId: z.string().uuid().nullable().optional(),
  sourcePeerGroupId: z.string().uuid().nullable().optional(),
  numberPattern: NumberPatternSchema,
  destinationType: z.enum(['peer', 'peerGroup', 'reject']).default('peer'),
  destinationTarget: z.string().default(''),
  conditions: RoutingConditionsSchema,
  actions: RoutingActionsSchema,
  inboundManipulationId: z.string().uuid().nullable().optional(),
  outboundManipulationId: z.string().uuid().nullable().optional(),
  recordRouteEnabled: z.boolean().default(true),
  decrementMaxForwards: z.boolean().default(true),
  insertViaHeader: z.boolean().default(true),
  routeHeaders: z.array(z.string()).optional(),
  rfcConditions: RfcConditionsSchema,
}).superRefine(refineResolvedSourceScope);

export type RoutingRuleCreateInputStrict = z.infer<typeof RoutingRuleCreateInputStrictSchema>;

export const RoutingRuleUpdateInputStrictSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  priority: z.number().min(1).max(999).optional(),
  sourceScope: RoutingRuleSourceScopeSchema.optional(),
  sourcePeerId: z.string().uuid().nullable().optional(),
  sourcePeerGroupId: z.string().uuid().nullable().optional(),
  numberPattern: NumberPatternSchema,
  destinationType: z.enum(['peer', 'peerGroup', 'reject']).optional(),
  destinationTarget: z.string().optional(),
  conditions: RoutingConditionsSchema,
  actions: RoutingActionsSchema,
  inboundManipulationId: z.string().uuid().nullable().optional(),
  outboundManipulationId: z.string().uuid().nullable().optional(),
  recordRouteEnabled: z.boolean().optional(),
  decrementMaxForwards: z.boolean().optional(),
  insertViaHeader: z.boolean().optional(),
  routeHeaders: z.array(z.string()).optional(),
  rfcConditions: RfcConditionsSchema,
}).superRefine(refineRoutingRuleUpdate);

export type RoutingRuleUpdateInputStrict = z.infer<typeof RoutingRuleUpdateInputStrictSchema>;

// ========================================
// TYPE DEFINITIONS
// ========================================

/**
 * Type for time window configuration
 */
export type TimeWindow = z.infer<typeof TimeWindowSchema>;

/**
 * Type for routing conditions
 */
export type RoutingConditions = z.infer<typeof RoutingConditionsSchema>;

/**
 * Type for routing actions
 */
export type RoutingActions = z.infer<typeof RoutingActionsSchema>;

/**
 * Type for RFC 3261 conditions
 */
export type RfcConditions = z.infer<typeof RfcConditionsSchema>;

/**
 * Type for creating a Routing Rule
 */
export type RoutingRuleCreateInput = z.infer<typeof RoutingRuleCreateInputSchema>;

/**
 * Type for updating a Routing Rule
 */
export type RoutingRuleUpdateInput = z.infer<typeof RoutingRuleUpdateInputSchema>;

/**
 * Type for number pattern type
 */
export type NumberPatternType = z.infer<typeof NumberPatternTypeSchema>;

/**
 * Type for single number pattern
 */
export type SingleNumberPattern = z.infer<typeof SingleNumberPatternSchema>;

/**
 * Type for combined number pattern (called + calling)
 */
export type NumberPattern = z.infer<typeof NumberPatternSchema>;

/**
 * Type for source type
 */
export type SourceType = z.infer<typeof SourceTypeSchema>;

// ========================================
// PATTERN TRANSFORMATION UTILITIES
// ========================================

/**
 * Escape special regex characters in a string
 * Used when converting user input to regex patterns
 */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Convert user-friendly pattern to regex
 * 
 * @example
 * patternToRegex('prefix', '+1') // returns '^\+1'
 * patternToRegex('suffix', '@teams.com') // returns '@teams\.com$'
 * patternToRegex('exact', '+12025551234') // returns '^\+12025551234$'
 * patternToRegex('contains', 'mobile') // returns '.*mobile.*'
 * patternToRegex('any', '') // returns '.*'
 * patternToRegex('regex', '^\\+1[2-9]') // returns '^\\+1[2-9]' (passthrough)
 */
export function patternToRegex(type: NumberPatternType, value: string): string {
  if (type === 'any' || !value) {
    return '.*';
  }
  
  if (type === 'regex') {
    // Raw regex - pass through as-is
    return value;
  }
  
  const escaped = escapeRegex(value);
  
  switch (type) {
    case 'prefix':
      return `^${escaped}`;
    case 'suffix':
      return `${escaped}$`;
    case 'exact':
      return `^${escaped}$`;
    case 'contains':
      return `.*${escaped}.*`;
    default:
      return '.*';
  }
}

/**
 * Try to detect pattern type from a regex string
 * Used for backward compatibility when displaying existing rules
 * 
 * @returns The detected pattern type and extracted value
 */
export function regexToPattern(regex: string): { type: NumberPatternType; value: string } {
  if (!regex || regex === '.*' || regex === '*') {
    return { type: 'any', value: '' };
  }
  
  // Check for exact match: ^value$
  if (regex.startsWith('^') && regex.endsWith('$') && !regex.includes('.*')) {
    const value = regex.slice(1, -1);
    // Unescape common characters
    const unescaped = value.replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
    return { type: 'exact', value: unescaped };
  }
  
  // Check for prefix: ^value (not ending with $)
  if (regex.startsWith('^') && !regex.endsWith('$') && !regex.includes('.*')) {
    const value = regex.slice(1);
    const unescaped = value.replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
    return { type: 'prefix', value: unescaped };
  }
  
  // Check for suffix: value$ (not starting with ^)
  if (regex.endsWith('$') && !regex.startsWith('^') && !regex.includes('.*')) {
    const value = regex.slice(0, -1);
    const unescaped = value.replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
    return { type: 'suffix', value: unescaped };
  }
  
  // Check for contains: .*value.*
  if (regex.startsWith('.*') && regex.endsWith('.*')) {
    const value = regex.slice(2, -2);
    const unescaped = value.replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
    return { type: 'contains', value: unescaped };
  }
  
  // Can't detect - return as raw regex
  return { type: 'regex', value: regex };
}

/**
 * Convert NumberPattern to conditions object for backward compatibility
 * This generates the legacy fromPattern/toPattern fields for the engine
 */
export function numberPatternToConditions(pattern: NumberPattern | null | undefined): {
  fromPattern?: string;
  toPattern?: string;
} {
  if (!pattern) {
    return {};
  }
  
  const result: { fromPattern?: string; toPattern?: string } = {};
  
  if (pattern.called && pattern.called.type !== 'any') {
    result.toPattern = patternToRegex(pattern.called.type, pattern.called.value);
  }
  
  if (pattern.calling && pattern.calling.type !== 'any') {
    result.fromPattern = patternToRegex(pattern.calling.type, pattern.calling.value);
  }
  
  return result;
}

// ============================================================================
// CANONICAL ROUTING-RULE MATCHER
// ----------------------------------------------------------------------------
// Single source of truth for evaluating a routing rule's structured number
// patterns. The sip-engine runtime MUST evaluate number patterns through
// these helpers rather than reimplementing matching inline, so pattern
// semantics live in one place and are unit-testable in core.
//
// Semantics (preserve the existing sip-engine behavior):
// - Matching is CASE-SENSITIVE for all pattern types.
// - `prefix`/`suffix`/`exact` are anchored; `contains` is a substring match;
//   `regex` is an unanchored, case-sensitive `RegExp.test`.
// - An invalid `regex` value never matches (returns false) rather than throwing.
// - `any` (or an empty/undefined pattern) always matches.
// ============================================================================

/**
 * Match a single candidate string (an already-extracted number/user part)
 * against one structured number pattern.
 *
 * This is the canonical matching primitive. It builds the anchored regex via
 * {@link patternToRegex} and applies it case-sensitively, which is equivalent
 * to the previous inline `startsWith`/`endsWith`/`===`/`includes`/`RegExp`
 * checks while adding safe handling for invalid user-supplied regex values.
 */
export function matchesNumberPattern(
  candidate: string,
  pattern: SingleNumberPattern | null | undefined,
): boolean {
  if (!pattern || pattern.type === 'any') {
    return true;
  }

  const regexSource = patternToRegex(pattern.type, pattern.value);
  try {
    return new RegExp(regexSource).test(candidate);
  } catch {
    // Invalid user-supplied regex must not crash routing; it simply never matches.
    return false;
  }
}

/**
 * Evaluate a routing rule's combined called/calling number pattern against
 * already-extracted candidate numbers. Returns true when both configured
 * sub-patterns match (an absent sub-pattern matches anything).
 */
export function matchesRoutingNumberPattern(
  numberPattern: NumberPattern | null | undefined,
  candidates: { readonly called: string; readonly calling: string },
): boolean {
  if (!numberPattern) {
    return true;
  }

  return (
    matchesNumberPattern(candidates.called, numberPattern.called) &&
    matchesNumberPattern(candidates.calling, numberPattern.calling)
  );
}
